import { cx } from '@emotion/css';
import AddIcon from '@mui/icons-material/Add';
import Divider from '@mui/material/Divider';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { ix } from '../../../../../../utils/ix';
import { Button, CircularProgress } from '../../../../../atoms';
import { InputField } from '../../../../formFields';
import { getCommonViewPermissions } from '../../ViewsMenu.service';
import { CustomView } from '../CustomView';
import { PredefinedView } from '../PredefinedView';
import { useStyles } from './MenuContent.styles';
export const MenuContent = memo(({ activeView, className, commonViewsPermissions, id, loading, maxNameLength, onCreate, onClick, onDelete, onEdit, onPin, onUnpin, views, }) => {
    const classes = useStyles();
    const [createdName, setCreatedName] = useState('');
    const [isCreateModeActive, setIsCreateModeActive] = useState(false);
    const [isWaitingForActionConfirmation, setIsWaitingForActionConfirmation] = useState(false);
    const filterOutActiveView = useCallback((view) => (activeView ? view.id !== activeView.id : true), [activeView]);
    const sortByName = useCallback((firstView, secondView) => firstView.name.localeCompare(secondView.name), []);
    const customViews = views.filter((view) => view.type === 'custom').filter(filterOutActiveView);
    const customViewsPinned = customViews.filter((view) => view.pinned).sort(sortByName);
    const customViewsUnpinned = customViews.filter((view) => !view.pinned).sort(sortByName);
    const sortedCustomViews = [...customViewsPinned, ...customViewsUnpinned];
    const sortedPredefinedViews = views
        .filter((view) => view.type === 'predefined')
        .filter(filterOutActiveView)
        .sort(sortByName);
    const [isCreatingAllowed] = useMemo(() => {
        const { create } = getCommonViewPermissions(commonViewsPermissions);
        return [create];
    }, [commonViewsPermissions]);
    const handleCancel = useCallback(() => {
        setIsWaitingForActionConfirmation(false);
    }, []);
    const handleCreateNewClick = useCallback((e) => {
        e.stopPropagation();
        setIsCreateModeActive(true);
        setIsWaitingForActionConfirmation(true);
    }, []);
    const handleCreateNewCancel = useCallback((e) => {
        e.stopPropagation();
        setCreatedName('');
        setIsCreateModeActive(false);
        setIsWaitingForActionConfirmation(false);
    }, []);
    const handleCreateNewConfirm = useCallback((e) => {
        e.stopPropagation();
        onCreate(createdName);
        setCreatedName('');
        setIsCreateModeActive(false);
        setIsWaitingForActionConfirmation(false);
    }, [createdName, onCreate]);
    const handleCreateInputClick = useCallback((e) => {
        e.stopPropagation();
    }, []);
    const handleCreateInputChange = useCallback((e) => {
        setCreatedName(e.target.value);
    }, []);
    const handleCreateInputKeyDown = useCallback((e) => {
        e.stopPropagation();
    }, []);
    const handleClickView = useCallback((view) => () => {
        onClick(view);
    }, [onClick]);
    const handleDeleteView = useCallback((view) => () => {
        onDelete(view);
        setIsWaitingForActionConfirmation(false);
    }, [onDelete]);
    const handleEditView = useCallback((view) => (newViewName) => {
        onEdit(view, newViewName);
        setIsWaitingForActionConfirmation(false);
    }, [onEdit]);
    const handlePinView = useCallback((view) => () => {
        onPin(view);
    }, [onPin]);
    const handleUnpinView = useCallback((view) => () => {
        onUnpin(view);
    }, [onUnpin]);
    const handleViewConfirmationAwait = useCallback(() => {
        setIsWaitingForActionConfirmation(true);
    }, []);
    const createdNameExceedsMaxLength = createdName.length > maxNameLength;
    const renderActiveView = () => {
        if (!activeView) {
            return null;
        }
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: cx(className, classes.activeView), id: ix(id, 'active-view') }, activeView.type === 'predefined' ? (React.createElement(PredefinedView, { key: activeView.id, id: ix(id, 'active-view-predefined'), view: activeView, onClick: handleClickView(activeView), active: true })) : (React.createElement(CustomView, { key: activeView.id, id: ix(id, 'active-view-custom'), disabled: isWaitingForActionConfirmation, maxNameLength: maxNameLength, onCancel: handleCancel, onClick: handleClickView(activeView), onConfirmationAwait: handleViewConfirmationAwait, onDelete: handleDeleteView(activeView), onEdit: handleEditView(activeView), onPin: handlePinView(activeView), onUnpin: handleUnpinView(activeView), view: activeView, active: true }))),
            React.createElement(Divider, null)));
    };
    const renderPredefinedViews = () => {
        if (!sortedPredefinedViews.length) {
            return null;
        }
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: classes.predefinedViews, id: ix(id, 'predefined-views') }, sortedPredefinedViews.map((view) => (React.createElement(PredefinedView, { key: view.id, id: ix(id, 'predefined-view', view.id), disabled: isWaitingForActionConfirmation, onClick: handleClickView(view), view: view })))),
            React.createElement(Divider, null)));
    };
    const renderCustomViewCreationPanel = () => (React.createElement("div", { className: classes.createContainer },
        React.createElement(InputField, { id: ix(id, 'create-view-input'), error: createdNameExceedsMaxLength, helperText: createdNameExceedsMaxLength ? 'Provided name is too long' : undefined, value: createdName, onChange: handleCreateInputChange, onKeyDown: handleCreateInputKeyDown, placeholder: "Name your analytic view", textFieldProps: {
                variant: 'outlined',
                onMouseDown: handleCreateInputClick,
            } }),
        React.createElement(Button, { id: ix(id, 'create-view-cancel-btn'), color: "secondary", onMouseDown: handleCreateNewCancel }, "Cancel"),
        React.createElement(Button, { id: ix(id, 'create-view-confirm-btn'), variant: "outlined", onMouseDown: handleCreateNewConfirm, disabled: !createdName || createdNameExceedsMaxLength }, "Save")));
    const renderCreateCustomViewButton = () => {
        if (!isCreatingAllowed) {
            return null;
        }
        return (React.createElement(Button, { id: ix(id, 'create-view-btn'), className: classes.createButton, disabled: isWaitingForActionConfirmation, onClick: handleCreateNewClick },
            React.createElement(AddIcon, { className: classes.createIcon }),
            React.createElement("span", null, "Create New View")));
    };
    const renderCustomViews = () => {
        return (React.createElement("div", { className: classes.customViews, id: ix(id, 'custom-views') },
            isCreateModeActive ? renderCustomViewCreationPanel() : renderCreateCustomViewButton(),
            React.createElement("div", { className: classes.customViewsInner }, sortedCustomViews.map((view) => (React.createElement(CustomView, { key: view.id, id: ix(id, 'custom-view', view.id), disabled: isWaitingForActionConfirmation, maxNameLength: maxNameLength, onCancel: handleCancel, onConfirmationAwait: handleViewConfirmationAwait, onClick: handleClickView(view), onDelete: handleDeleteView(view), onEdit: handleEditView(view), onPin: handlePinView(view), onUnpin: handleUnpinView(view), view: view }))))));
    };
    return (React.createElement("div", { className: classes.root, id: ix(id, 'menu') },
        renderActiveView(),
        renderPredefinedViews(),
        renderCustomViews(),
        loading && (React.createElement("div", { className: classes.loadingIndicator },
            React.createElement(CircularProgress, null)))));
});
//# sourceMappingURL=MenuContent.js.map